import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FaRocket, 
  FaMobile, 
  FaSearch, 
  FaShoppingCart, 
  FaPalette, 
  FaArrowLeft,
  FaGlobe,
  FaChartLine,
  FaShieldAlt,
  FaEye,
  FaCogs,
  FaBuilding,
  FaEdit,
  FaComments
} from 'react-icons/fa';
import './ServicePage.css';
import WhatsappFloat from '../components/WhatsappFloat';
import { Helmet } from 'react-helmet-async';

const CriacaoSites = () => {
  const navigate = useNavigate();

  // Scroll para o topo quando a página carrega
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  return (
    <>
      <Helmet>
        <title>Criação de Sites | MicroCodie</title>
        <meta name="description" content="Sites profissionais, responsivos e otimizados para SEO. Crie sua presença digital com a MicroCodie." />
        <meta property="og:title" content="Criação de Sites | MicroCodie" />
        <meta property="og:description" content="Sites profissionais, responsivos e otimizados para SEO. Crie sua presença digital com a MicroCodie." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.microcodie.com.br/servicos/criacao-sites" />
        <meta property="og:image" content="/logo192.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Criação de Sites | MicroCodie" />
        <meta name="twitter:description" content="Sites profissionais, responsivos e otimizados para SEO. Crie sua presença digital com a MicroCodie." />
        <meta name="twitter:image" content="/logo192.png" />
        <link rel="canonical" href="https://www.microcodie.com.br/servicos/criacao-sites" />
      </Helmet>
      <div className="service-page">
        <div className="service-header">
          <div className="container">
            <motion.button 
              className="back-button"
              onClick={() => navigate('/')}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <FaArrowLeft />
              Voltar
            </motion.button>
            
            <motion.div 
              className="service-hero"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="service-icon-large">
                <FaGlobe />
              </div>
              <h1>Seu site é o seu cartão de visitas no digital</h1>
              <p className="service-subtitle">
                Crie uma presença online profissional, confiável e que gera resultados
              </p>
              <button 
                className="btn btn-primary hero-cta"
                onClick={() => navigate('/#contato')}
              >
                Solicitar orçamento
              </button>
            </motion.div>
          </div>
        </div>

        <div className="service-content">
          <div className="container">
            {/* Seção: Por que sua empresa precisa de um site HOJE? */}
            <motion.div 
              className="why-website-section"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2>Por que sua empresa precisa de um site HOJE?</h2>
              
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-number">80%</div>
                  <div className="stat-description">
                    dos potenciais clientes podem ser perdidos por empresas que não têm um site
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">81%</div>
                  <div className="stat-description">
                    das decisões de compra começam com uma pesquisa online
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">46%</div>
                  <div className="stat-description">
                    dos usuários julgam a credibilidade de uma empresa pelo design do site
                  </div>
                </div>
              </div>

              <div className="importance-grid">
                <div className="importance-item">
                  <FaSearch className="importance-icon" />
                  <h3>SEO e Visibilidade</h3>
                  <p>
                    Sites otimizados aparecem nos primeiros resultados do Google, 
                    aumentando significativamente sua visibilidade e atraindo mais clientes.
                  </p>
                </div>
                <div className="importance-item">
                  <FaShieldAlt className="importance-icon" />
                  <h3>Autoridade e Confiança</h3>
                  <p>
                    Um site profissional transmite credibilidade e confiança, 
                    essenciais para converter visitantes em clientes.
                  </p>
                </div>
                <div className="importance-item">
                  <FaChartLine className="importance-icon" />
                  <h3>Conversão e Resultados</h3>
                  <p>
                    Sites bem estruturados convertem visitantes em leads e vendas, 
                    gerando resultados reais para seu negócio.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Seção: Tipos de sites que criamos */}
            <motion.div 
              className="site-types-section"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h2>Tipos de sites que criamos</h2>
              <div className="site-types-grid">
                <div className="site-type-card">
                  <FaBuilding className="site-type-icon" />
                  <h3>Sites Institucionais</h3>
                  <p>Presença profissional da sua empresa na internet com informações completas sobre produtos e serviços</p>
                </div>
                <div className="site-type-card">
                  <FaRocket className="site-type-icon" />
                  <h3>Landing Pages</h3>
                  <p>Páginas focadas em conversão para campanhas específicas e captação de leads</p>
                </div>
                <div className="site-type-card">
                  <FaPalette className="site-type-icon" />
                  <h3>Portfólios</h3>
                  <p>Apresentação profissional do seu trabalho e projetos para atrair novos clientes</p>
                </div>
                <div className="site-type-card">
                  <FaShoppingCart className="site-type-icon" />
                  <h3>E-commerce</h3>
                  <p>Lojas virtuais completas com sistema de pagamento, gestão de produtos e vendas online</p>
                </div>
                <div className="site-type-card">
                  <FaEdit className="site-type-icon" />
                  <h3>Blogs</h3>
                  <p>Plataformas de conteúdo para posicionamento de marca e geração de autoridade</p>
                </div>
                <div className="site-type-card">
                  <FaComments className="site-type-icon" />
                  <h3>Outros</h3>
                  <p>Sites personalizados para necessidades específicas do seu negócio</p>
                </div>
              </div>
            </motion.div>

            {/* Seção: Portfólio Interativo */}
            {/* <motion.div 
              className="portfolio-section"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <h2>Portfólio</h2>
              <div className="portfolio-grid">
                <div className="portfolio-item">
                  <div className="portfolio-preview">
                    <iframe 
                      src="https://via.placeholder.com/400x300/00AFEF/FFFFFF?text=Site+Institucional"
                      title="Site Institucional"
                      className="portfolio-iframe"
                    />
                    <div className="portfolio-overlay">
                      <FaEye className="overlay-icon" />
                      <span>Ver projeto</span>
                    </div>
                  </div>
                  <div className="portfolio-info">
                    <h3>Site Institucional - Equilíbrio Tributário</h3>
                    <p className="portfolio-segment">Contabilidade</p>
                  </div>
                </div>
                
                <div className="portfolio-item">
                  <div className="portfolio-preview">
                    <iframe 
                      src="https://via.placeholder.com/400x300/0081A5/FFFFFF?text=E-commerce"
                      title="E-commerce"
                      className="portfolio-iframe"
                    />
                    <div className="portfolio-overlay">
                      <FaEye className="overlay-icon" />
                      <span>Ver projeto</span>
                    </div>
                  </div>
                  <div className="portfolio-info">
                    <h3>Eventos - Inova</h3>
                    <p className="portfolio-segment">Educação</p>
                  </div>
                </div>
                
                <div className="portfolio-item">
                  <div className="portfolio-preview">
                    <iframe 
                      src="https://via.placeholder.com/400x300/606062/FFFFFF?text=Landing+Page"
                      title="Landing Page"
                      className="portfolio-iframe"
                    />
                    <div className="portfolio-overlay">
                      <FaEye className="overlay-icon" />
                      <span>Ver projeto</span>
                    </div>
                  </div>
                  <div className="portfolio-info">
                    <h3>Software - Collecta</h3>
                    <p className="portfolio-segment">Aplicativo</p>
                  </div>
                </div>
              </div>
            </motion.div> */}

            {/* Seção: Como funciona o processo */}
            <motion.div 
              className="process-section"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <h2>Como funciona o processo</h2>
              <div className="process-timeline">
                <div className="process-step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h3>Briefing</h3>
                    <p>Entendimento das necessidades, objetivos e público-alvo do projeto</p>
                  </div>
                </div>
                <div className="process-step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h3>Design</h3>
                    <p>Criação de layouts e protótipos visuais para aprovação</p>
                  </div>
                </div>
                <div className="process-step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h3>Aprovação</h3>
                    <p>Validação dos designs e ajustes conforme feedback</p>
                  </div>
                </div>
                <div className="process-step">
                  <div className="step-number">4</div>
                  <div className="step-content">
                    <h3>Desenvolvimento</h3>
                    <p>Codificação do site seguindo as melhores práticas</p>
                  </div>
                </div>
                <div className="process-step">
                  <div className="step-number">5</div>
                  <div className="step-content">
                    <h3>Publicação</h3>
                    <p>Deploy em produção e configuração final</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Seção: Diferenciais */}
            <motion.div 
              className="differentials-section"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <h2>Nossos Diferenciais</h2>
              <div className="differentials-grid">
                <div className="differential-card">
                  <FaRocket className="differential-icon" />
                  <h3>Performance</h3>
                  <p>Sites ultra-rápidos que carregam em segundos e proporcionam a melhor experiência</p>
                </div>
                <div className="differential-card">
                  <FaSearch className="differential-icon" />
                  <h3>SEO Otimizado</h3>
                  <p>Otimização completa para mecanismos de busca, aumentando sua visibilidade</p>
                </div>
                <div className="differential-card">
                  <FaMobile className="differential-icon" />
                  <h3>Mobile First</h3>
                  <p>Design responsivo que funciona perfeitamente em todos os dispositivos</p>
                </div>
                <div className="differential-card">
                  <FaCogs className="differential-icon" />
                  <h3>Integração</h3>
                  <p>Possibilidade de integração com ferramentas essenciais como Google Analytics, CRM e redes sociais</p>
                </div>
              </div>
            </motion.div>

            {/* Seção: CTA Final */}
            <motion.div 
              className="final-cta-section"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              <h2>Quer um site profissional? Fale com a gente agora mesmo</h2>
              <p>
                Não perca mais oportunidades por não ter uma presença digital profissional. 
                Entre em contato e vamos criar o site que vai impulsionar seu negócio.
              </p>
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/#contato')}
              >
                Fale com a gente
              </button>
            </motion.div>
          </div>
        </div>
        <WhatsappFloat />
      </div>
    </>
  );
};

export default CriacaoSites; 